import { IconType } from "react-icons";
import { NavLink } from "react-router-dom";
import {
  PiBookOpenText,
  PiBookOpenTextFill,
  PiBookmarksSimple,
  PiBookmarksSimpleFill,
  PiCards,
  PiCardsFill,
} from "react-icons/pi";
import { lstr } from "./localization";
import { useLoggedIn } from "./firebase";
import { usePracticeStatusQuery } from "./practiceApi";

// The app's main sections for logged-in users: shown as tabs in the header
// on wider screens and as a tab bar at the bottom of the screen on phones.
interface Section {
  to: string;
  label: string;
  icon: IconType;
  activeIcon: IconType;
  // Small count on the tab, e.g. words due for review.
  badge?: number;
}

function useSections(l: string, r: string): Section[] {
  const strings = lstr(l);
  const practiceStatus = usePracticeStatusQuery(r, useLoggedIn());
  return [
    {
      to: "/",
      label: strings.nav_stories,
      icon: PiBookOpenText,
      activeIcon: PiBookOpenTextFill,
    },
    {
      to: "/practice",
      label: strings.practice_nav,
      icon: PiCards,
      activeIcon: PiCardsFill,
      badge: practiceStatus.data?.due,
    },
    {
      to: "/dictionary",
      label: strings.nav_words,
      icon: PiBookmarksSimple,
      activeIcon: PiBookmarksSimpleFill,
    },
  ];
}

function Badge({ count, className }: { count: number; className: string }) {
  return (
    <span
      // Keyed by count so a change replays the bump.
      key={count}
      className={`animate-bump inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-highlight px-1 text-[11px] font-bold leading-none text-on-highlight ${className}`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function DesktopNav({
  l,
  r,
  className = "",
}: {
  l: string;
  r: string;
  className?: string;
}) {
  const sections = useSections(l, r);
  return (
    <nav className={`items-center gap-1 ${className}`}>
      {sections.map((section) => (
        <NavLink
          key={section.to}
          to={section.to}
          end
          className={({ isActive }) =>
            `flex h-9 items-center gap-2 rounded-full px-3.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-cream-dark text-main-text"
                : "text-secondary-text hover:bg-cream-dark/60 hover:text-main-text"
            }`
          }
        >
          {({ isActive }) => {
            const Icon = isActive ? section.activeIcon : section.icon;
            return (
              <>
                <Icon size={17} className={isActive ? "text-primary" : ""} />
                {section.label}
                {!!section.badge && (
                  <Badge count={section.badge} className="" />
                )}
              </>
            );
          }}
        </NavLink>
      ))}
    </nav>
  );
}

// BottomTabBar is the phone navigation, in thumb reach. It stays above the
// home indicator on notched phones (viewport-fit=cover in index.html).
export function BottomTabBar({ l, r }: { l: string; r: string }) {
  const sections = useSections(l, r);
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-cream/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md sm:hidden">
      <div className="mx-auto grid h-16 max-w-md grid-cols-3">
        {sections.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            end
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors active:scale-95 ${
                isActive ? "text-primary" : "text-secondary-text"
              }`
            }
          >
            {({ isActive }) => {
              const Icon = isActive ? section.activeIcon : section.icon;
              return (
                <>
                  <span className="relative">
                    <Icon size={24} />
                    {!!section.badge && (
                      <Badge
                        count={section.badge}
                        className="absolute -right-3 -top-1.5 ring-2 ring-cream"
                      />
                    )}
                  </span>
                  {section.label}
                </>
              );
            }}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
